import { ServiceAssignment, ServiceItem } from '../types';
import { assignmentLabel } from './assignmentLabels';
import { resolveFlatCoverage } from './flatRateLogic';

/**
 * Funktion (Zuordnung) einer LV-Position im LAUFENDEN Projekt umstellen —
 * im Dialog „Leistungen Details" je Position, nur fuer Admins.
 *
 * WARUM: bisher liess sich die Zuordnung nur beim Anlegen (ProjectSetupWizard)
 * und im Admin-Katalog setzen. Aus Alt-Rechnungen uebernommene Positionen
 * tragen `none` und blieben so stehen — Projekt 5634 bot „Honorar
 * Vorankündigung" deshalb bei jedem Versand als Zusatzleistung an, und
 * niemand konnte es aendern.
 *
 * Die Zuordnung steuert nur, WO eine Position zur Buchung angeboten und
 * gezaehlt wird. Bisherige Buchungen bleiben unveraendert — sie haengen an
 * der Position, nicht an ihrer Funktion.
 */

/**
 * Waehlbare Funktionen — dieselbe Liste wie im Anlege-Assistenten, OHNE
 * 'flatrate' (entsteht nur ueber das Deckungs-Select, sonst halb angelegte
 * Pauschale ohne Deckung) und ohne 'unterweisung' (gehoert der SiFa-Betreuung).
 * 'gbu' (Gefaehrdungsbeurteilung, SiFa) ist waehlbar: die Zuordnung ist
 * optional (docs/sifa-gbu-plan.md O3), eine Sammelposition wie
 * „Arbeitsschutzdokumentation" laesst sich so nachtraeglich festlegen.
 */
export const ASSIGNMENT_CHOICES: ReadonlyArray<{ id: ServiceAssignment; label: string }> = [
    { id: 'none', label: 'Keine / Sonstige' },
    { id: 'inspection', label: 'Begehung' },
    { id: 'vorank', label: 'Vorankündigung' },
    { id: 'unterlage', label: 'Unterlage (RAB)' },
    { id: 'sigeplan', label: 'SiGe-Plan' },
    { id: 'asplan', label: 'A+S-Plan' },
    { id: 'einweisung', label: 'Einweisung SiGe-Plan' },
    { id: 'site_rules', label: 'Baustellenordnung' },
    { id: 'planning_general', label: 'Planung Allg.' },
    { id: 'additional', label: 'Zusatzleistung' },
    { id: 'gbu', label: 'Gefährdungsbeurteilung (SiFa)' },
];

/** Fehlende Zuordnung ist `none` — so schreibt sie auch die Datenbank. */
export const currentAssignment = (s: Pick<ServiceItem, 'assignment'>): ServiceAssignment => s.assignment ?? 'none';

/**
 * Monatspauschale und SiFa-Unterweisung bleiben, wie sie sind: die Pauschale
 * traegt ihre Deckung (flatRate), die beim Umstellen still wegfiele und
 * gedeckte Begehungen wieder einzeln abrechenbar machte; die Unterweisung
 * zaehlt die SiFa-Betreuung.
 */
export const canChangeAssignment = (s: Pick<ServiceItem, 'assignment' | 'flatRate'>): boolean =>
    s.assignment !== 'flatrate' && !s.flatRate && s.assignment !== 'unterweisung';

export const assignmentChoiceLabel = (assignment: ServiceAssignment | undefined): string =>
    ASSIGNMENT_CHOICES.find(c => c.id === (assignment ?? 'none'))?.label ?? assignmentLabel(assignment, 'lang');

/** Deckt eine Monatspauschale danach andere Positionen als vorher? */
const coverageDiffers = (before: ReadonlyArray<ServiceItem>, after: ReadonlyArray<ServiceItem>): boolean => {
    const a = resolveFlatCoverage([...before]);
    const b = resolveFlatCoverage([...after]);
    if (a.size !== b.size) return true;
    for (const [id, flatId] of a) if (b.get(id) !== flatId) return true;
    return false;
};

/**
 * Neue Leistungsliste mit umgestellter Funktion — null, wenn nichts zu tun
 * oder nicht erlaubt ist (unbekannte Position, gleicher Wert, Wert ausserhalb
 * der Auswahl, gesperrte Position).
 *
 * Ebenfalls null, wenn die Umstellung aendert, was eine Monatspauschale
 * DECKT: eine Pauschale mit Umfang „Begehungen" deckt jede Position mit
 * Zuordnung 'inspection' (resolveFlatCoverage). Eine gedeckte Begehung auf
 * „Zusatzleistung" gestellt waere still wieder einzeln abrechenbar, eine
 * Zusatzleistung auf „Begehung" verschwaende still in der Pauschale — beides
 * ist eine Geldentscheidung und gehoert in die Einrichtung der Pauschale,
 * nicht in ein Auswahlfeld.
 */
export const changeServiceAssignment = (
    services: ReadonlyArray<ServiceItem>,
    serviceId: string,
    next: ServiceAssignment,
): ServiceItem[] | null => {
    if (!ASSIGNMENT_CHOICES.some(c => c.id === next)) return null;
    const target = services.find(s => s.id === serviceId);
    if (!target || !canChangeAssignment(target)) return null;
    if (currentAssignment(target) === next) return null;
    const changed = services.map(s => (s.id === serviceId ? { ...s, assignment: next } : s));
    if (coverageDiffers(services, changed)) return null;
    return changed;
};

/**
 * Funktionen, die fuer diese Position gerade NICHT waehlbar sind, weil sie die
 * Deckung einer Monatspauschale verschoeben (siehe changeServiceAssignment).
 * Der aktuelle Wert ist nie gesperrt. Fuer das Auswahlfeld: ausgegraut statt
 * eines Klicks, der nichts tut.
 */
export const blockedAssignmentChoices = (
    services: ReadonlyArray<ServiceItem>,
    serviceId: string,
): Set<ServiceAssignment> => {
    const target = services.find(s => s.id === serviceId);
    if (!target || !canChangeAssignment(target)) return new Set();
    const current = currentAssignment(target);
    return new Set(
        ASSIGNMENT_CHOICES
            .filter(c => c.id !== current && changeServiceAssignment(services, serviceId, c.id) === null)
            .map(c => c.id),
    );
};

/** Text der Notiz in der Mitteilungszentrale — damit die Umstellung nachvollziehbar bleibt. */
export const assignmentChangeNoteText = (
    service: Pick<ServiceItem, 'lvPosition' | 'title'>,
    from: ServiceAssignment | undefined,
    to: ServiceAssignment,
): string => {
    const position = [service.lvPosition, service.title ? `„${service.title}“` : ''].filter(Boolean).join(' ');
    return `Funktion der Position ${position} geändert: ${assignmentChoiceLabel(from)} → ${assignmentChoiceLabel(to)}`;
};
